import styled from "@emotion/styled";
import { motion } from "framer-motion";

// Styled Components
export const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
`;

export const Section = styled.section`
  margin-bottom: ${(props) => props.theme.spacing.xl};
`;

export const SectionTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

export const SectionSubtitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

export const AboutContent = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: left;
  margin: 0 auto;
  line-height: 1.8;
  font-size: 1.1rem;
  width: 100%;
  color: ${(props) => props.theme.colors.textLight};
`;

// Games Section Styles
export const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${(props) => props.theme.spacing.xl};
  margin-top: ${(props) => props.theme.spacing.lg};

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

export const GameCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${(props) => props.theme.shadows.lg};
  }
`;

export const GameTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;
export const GameDescription = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: ${(props) => props.theme.spacing.md};
  flex-grow: 1;
`;

export const FeaturedGameCard = styled(GameCard)`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: ${(props) => props.theme.spacing.lg};
  padding: ${(props) => props.theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 0 0 ${(props) => props.theme.spacing.lg} 0;
  }

  ${GameCard}:hover & {
    transform: translateY(-8px);
    box-shadow: ${(props) => props.theme.shadows.lg};
  }

  ${GameTitle} {
    font-size: 2.4rem;
  }

  ${GameDescription} {
    font-size: 1.4rem;
  }
`;

export const GameImage = styled.img`
  width: 100%;
  height: 320px;
  object-fit: cover;
  border-radius: ${(props) => props.theme.borderRadius.md};

  ${FeaturedGameCard} & {
    height: 400px;
    object-position: 50% 20%;
  }
`;

export const GameContent = styled.div`
  padding: ${(props) => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  ${FeaturedGameCard} & {
    padding: 0 ${(props) => props.theme.spacing.lg};
    align-items: flex-end;
  }
`;

export const GameMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

export const GameMetaItem = styled.span`
  background: rgba(255, 255, 255, 0.1);
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  font-size: 0.8rem;
  font-weight: 500;
  color: ${(props) => props.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const GameCTA = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
`;

export const PlatformLink = styled.a`
  display: inline-block;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  img {
    height: 80px;
    width: auto;
    max-width: 100%;
  }
`;

export const TitleLink = styled.a`
  display: inline-block;
  transition: transform 0.3s ease;
  height: 30px;
  margin-left: 10px;

  &:hover {
    opacity: 0.8;
  }

  img {
    height: 100%;
  }
`;

export const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

export const MemberImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  margin: 0 auto ${(props) => props.theme.spacing.md};
  border: 3px solid rgba(255, 255, 255, 0.3);
  transition: border-color 0.3s ease;
`;

export const TeamMember = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${(props) => props.theme.shadows.lg};
  }

  &:hover ${MemberImage} {
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

export const MemberName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

export const MemberRole = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: ${(props) => props.theme.spacing.sm};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const MemberBio = styled.p`
  color: ${(props) => props.theme.colors.textLight};
  line-height: 1.6;
  font-size: 0.95rem;
  margin: 0;
`;

export const MemberLinkedIn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.xs};
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: ${(props) => props.theme.spacing.sm};
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.sm};
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

export const LinkedInIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
  transition: filter 0.3s ease;
`;

export const MemberSocialLinks = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
  justify-content: center;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

export const EmailIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
  transition: filter 0.3s ease;
`;

export const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${(props) => props.theme.spacing.md};
  margin: ${(props) => props.theme.spacing.xl} 0;
`;

export const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.md};
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

export const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

export const StatLabel = styled.div`
  color: ${(props) => props.theme.colors.textLight};
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
